import { Strategy as StrategyType, VIDEO_TACTICS } from './utils'
import Styles from './StrategiesSelection.module.scss'
import { Radio } from 'components/RadioGroup'
import { RadioBullet } from 'components/RadioGroup/RadioBullet'
import { Heading } from 'blaze-ui/Heading/Heading'
import { Text } from 'blaze-ui/Text/Text'
import { pluralize } from 'utils'
import { ExternalIntegrationEnum } from 'api/clients/ExternalIntegrationsClient'
import { ProviderIcon } from 'screens/Calendar/WeekViewV2/CalendarEventCard/Icon'
import { Button } from 'blaze-ui/Button'
import Collapse from 'react-css-collapse'
import { useState } from 'react'
import { SelectedBlogPlatform, SelectedEmailPlatform } from 'types'
import { SOCIAL_TACTICS } from './utils'
import { TACTIC_TO_PROVIDER } from 'screens/blaze/Home/VirtualMarketerHome/Brain/PlatformSection/Types'
import { CustomizeTable } from './CustomizeTable/CustomizeTable'

export interface StrategyProps {
  strategy: StrategyType
  onSelect: (strategyId: string) => void
  onUpdate: (strategy: StrategyType) => void
  selectedBlogPlatform: SelectedBlogPlatform
  setSelectedBlogPlatform: (platform: SelectedBlogPlatform) => void
  selectedEmailPlatform: SelectedEmailPlatform
  setSelectedEmailPlatform: (platform: SelectedEmailPlatform) => void
}

export const Strategy = ({
  strategy,
  onSelect,
  onUpdate,
  selectedBlogPlatform,
  setSelectedBlogPlatform,
  selectedEmailPlatform,
  setSelectedEmailPlatform,
}: StrategyProps) => {
  const [isOpen, setIsOpen] = useState<boolean>(false)

  const handleToggle = () => {
    setIsOpen((open) => !open)
  }

  const seoContentsCount = strategy.seo_contents.find((content) => content.type === 'blog_post')?.count || 0
  const newsletterContentsCount = strategy.email_contents.find((content) => content.type === 'newsletter')?.count || 0
  const crosspostContentsCount = strategy.crosspost_contents.find((content) => content.type === 'post')?.count || 0
  const videoCrosspostContentsCount =
    strategy.video_crosspost_contents.find((content) => content.type === 'post')?.count || 0

  const socialTactics = strategy.tactics.filter((tactic) => SOCIAL_TACTICS.includes(tactic))
  const videoTactics = strategy.tactics.filter((tactic) => VIDEO_TACTICS.includes(tactic))

  return (
    <div className={Styles.strategyContainer}>
      <div
        className={Styles.radioWrapper}
        data-testid="strategy-radio-wrapper"
        onClick={() => {
          if (strategy.isSelected) {
            onSelect(strategy.id)
          }
        }}
      >
        <Radio value={strategy.id} size={24} hideRadio aria-label={strategy.name} className={Styles.radio}>
          {({ isSelected, isFocusVisible }) => (
            <RadioBullet isSelected={isSelected} isFocusVisible={isFocusVisible} size={24} />
          )}
        </Radio>
      </div>
      <div className={Styles.strategyContent}>
        <div className={Styles.strategyHeader}>
          <Heading level={3}>{strategy.name}</Heading>
          <Text variant="secondary" color="var(--dark-60)">
            {strategy.description}
          </Text>
        </div>
        <div className={Styles.strategyValues}>
          {crosspostContentsCount > 0 && (
            <>
              <div className={Styles.strategyValue}>
                <div className={Styles.providerIcons}>
                  {socialTactics.map((provider) => (
                    <ProviderIcon
                      key={provider}
                      provider={
                        TACTIC_TO_PROVIDER.find((t) => t.tactic === provider)?.provider as ExternalIntegrationEnum
                      }
                      size={16}
                      isHovered={false}
                      zIndex={0}
                      borderColor="rgba(247, 247, 247, 1)"
                      marginLeft={-5}
                    />
                  ))}
                </div>
                <Text variant="secondary">
                  {crosspostContentsCount} {pluralize('post', crosspostContentsCount)}
                </Text>
                <Text variant="secondary" color="var(--dark-40)">
                  /week
                </Text>
              </div>
              <div className={Styles.separator} />
            </>
          )}
          {videoCrosspostContentsCount > 0 && (
            <>
              <div className={Styles.strategyValue}>
                <div className={Styles.providerIcons}>
                  {videoTactics.map((provider) => (
                    <ProviderIcon
                      key={provider}
                      provider={
                        TACTIC_TO_PROVIDER.find((t) => t.tactic === provider)?.provider as ExternalIntegrationEnum
                      }
                      size={16}
                      isHovered={false}
                      zIndex={0}
                      isReels
                      borderColor="rgba(247, 247, 247, 1)"
                    />
                  ))}
                </div>
                <Text variant="secondary">
                  {videoCrosspostContentsCount} {pluralize('video', videoCrosspostContentsCount)}
                </Text>

                <Text variant="secondary" color="var(--dark-40)">
                  /week
                </Text>
              </div>
              <div className={Styles.separator} />
            </>
          )}
          {newsletterContentsCount > 0 && (
            <>
              <div className={Styles.strategyValue}>
                <Text variant="secondary">
                  {newsletterContentsCount} {pluralize('email', newsletterContentsCount)}
                </Text>
                <Text variant="secondary" color="var(--dark-40)">
                  /week
                </Text>
              </div>
              <div className={Styles.separator} />
            </>
          )}
          {seoContentsCount > 0 && (
            <>
              <div className={Styles.strategyValue}>
                <Text variant="secondary">
                  {seoContentsCount} {pluralize('blog', seoContentsCount)}
                </Text>
                <Text variant="secondary" color="var(--dark-40)">
                  /week
                </Text>
              </div>
            </>
          )}
          <Button variant="secondary" size="sm" withChevron={isOpen ? 'up' : 'down'} onPress={handleToggle}>
            Customize
          </Button>
        </div>
        <Collapse isOpen={isOpen} className={Styles.collapse}>
          <div className={Styles.customizeSeparator} />
          <CustomizeTable
            strategy={strategy}
            onUpdate={onUpdate}
            selectedBlogPlatform={selectedBlogPlatform}
            setSelectedBlogPlatform={setSelectedBlogPlatform}
            selectedEmailPlatform={selectedEmailPlatform}
            setSelectedEmailPlatform={setSelectedEmailPlatform}
          />
        </Collapse>
      </div>
    </div>
  )
}
